import { useCallback, useEffect, useMemo } from "react";

import { useSelector } from "react-redux";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useWindowSize from "../../hooks/useWindowSize";

import { Trans } from "react-i18next";
import { Ring } from "@uiball/loaders";
import { Controller } from "react-hook-form";

import Button from "../button";
import TextField from "../text-field";
import PhoneField from "../phone-field";
import SelectField from "../select-field";
import CheckboxField from "../checkbox-field";
import TextAreaField from "../text-area-field";

import {
  isTrue,
  isFalse,
  isEmpty,
  isEmptyList,
  isEmptyObject,
  equalsIgnoringCase,
} from "../../utils/validators";
import { logDebug, logError } from "../../utils/logger";
import useGetLocalePath from "../../hooks/useGetLocalePath.js";

function ContactForm(props) {
  const {
    form,
    loading,
    onValid,
    onInvalid,
    onConsentClick,
    onChangeDefaultValues,
  } = props;
  const { control, formState, handleSubmit } = form;
  const { isLoading, isDirty, disabled, defaultValues } = formState;

  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const { getLocalePath } = useGetLocalePath();

  const { ui } = useSelector((state) => state);
  const { contact_reason } = ui;
  const { settings } = useSelector((state) => state.settings);
  const { country } = settings;

  const watchedConsent = useWatch({ name: "consent", control: control });

  const options = useMemo(() => {
    return Object.entries(t("contact.form.reasons")).map((entry) => ({
      value: entry[0],
      label: entry[1],
    }));
  }, [t]);

  const defaultCountry = useMemo(() => {
    return !isEmpty(country) ? String(country).toLocaleLowerCase() : "us";
  }, [country]);

  const loadingForm = useMemo(() => {
    return [isTrue(loading), isTrue(isLoading)].some((i) => i);
  }, [loading, isLoading]);

  const disabledSubmit = useMemo(() => {
    return [
      isTrue(disabled),
      isFalse(isDirty),
      isTrue(loadingForm),
      isFalse(watchedConsent),
    ].some((i) => i);
  }, [loadingForm, disabled, isDirty, watchedConsent]);

  const textAreaStyle = useMemo(() => {
    return {
      height: `${windowSize.width >= 576 ? 55 : 25}px`,
    };
  }, [windowSize.width]);

  const onCopyHandler = useCallback((evt) => {
    logDebug("ContactForm", "onCopyHandler", { evt: evt });
    evt.preventDefault();
    evt.stopPropagation();
    return false;
  }, []);

  const onPasteHandler = useCallback((evt) => {
    logDebug("ContactForm", "onPasteHandler", { evt: evt });
    evt.preventDefault();
    evt.stopPropagation();
    return false;
  }, []);

  const onValidHandler = useCallback(
    (data, evt) => {
      const handler = async (data, evt) => {
        if (!disabled) {
          onValid && onValid(data, evt);
        }
      };
      logDebug("ContactForm", "onValidHandler", { data: data, evt: evt });
      handler(data, evt);
    },
    [disabled, onValid]
  );

  const onInvalidHandler = useCallback(
    (errors, evt) => {
      const handler = async (errors, evt) => {
        if (!disabled) {
          onInvalid && onInvalid(errors, evt);
        }
      };
      logError("ContactForm", "onInvalidHandler", {
        errors: errors,
        evt: evt,
      });
      handler(errors, evt);
    },
    [disabled, onInvalid]
  );

  const onClickConsentHandler = useCallback(
    (evt, type) => {
      logDebug("PersonalForm", "onClickConsentHandler", {
        evt: evt,
        type: type,
      });
      onConsentClick && onConsentClick(evt, type);
    },
    [onConsentClick]
  );

  const ConsentLabel = (props) => {
    const { onClick } = props;

    const onClickHandler = useCallback(
      (evt, type) => onClick && onClick(evt, type),
      [onClick]
    );

    return (
      <Trans
        i18nKey={"contact.form.consent.template"}
        components={{
          text: (
            <span
              title={t("contact.form.consent.text")}
              className="text"
            ></span>
          ),
          legal_link: (
            <span
              role="button"
              className="link legal"
              title={`https://${process.env.REACT_APP_DOMAIN}${getLocalePath(
                t("routes.public.legal")
              )}`}
              onClick={(evt) => onClickHandler(evt, "LEGAL")}
            ></span>
          ),
          cookie_link: (
            <span
              role="button"
              className="link cookie"
              title={`https://${process.env.REACT_APP_DOMAIN}${getLocalePath(
                t("routes.public.cookies")
              )}`}
              onClick={(evt) => onClickHandler(evt, "COOKIES")}
            ></span>
          ),
          privacy_link: (
            <span
              role="button"
              className="link privacy"
              title={`https://${process.env.REACT_APP_DOMAIN}${getLocalePath(
                t("routes.public.privacy")
              )}`}
              onClick={(evt) => onClickHandler(evt, "PRIVACY")}
            ></span>
          ),
        }}
        values={{
          legal: t("contact.form.consent.legal"),
          cookie: t("contact.form.consent.cookies"),
          privacy: t("contact.form.consent.privacy"),
        }}
      />
    );
  };

  useEffect(() => {
    if (!isEmptyList(options) && !isEmpty(contact_reason)) {
      const reason = options.find((o) =>
        equalsIgnoringCase(contact_reason, o.value)
      );
      if (!isEmptyObject(reason)) {
        onChangeDefaultValues &&
          onChangeDefaultValues({
            ...defaultValues,
            reason: reason,
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, contact_reason]);

  return (
    <div className="contact-form__container">
      {loadingForm && (
        <div className="contact-form__loading">
          <Ring size={100} lineWeight={5} speed={2} color="#777e8b" />
        </div>
      )}
      <form
        id="contact_form"
        className="contact-form__form"
        onSubmit={handleSubmit(onValidHandler, onInvalidHandler)}
      >
        <div className="field name">
          <Controller
            name="name"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                label={t("contact.form.name")}
                placeholder={t("contact.form.name")}
                autoComplete={"name"}
                error={fieldState?.error?.message}
                required
              />
            )}
          />
        </div>
        <div className="field email">
          <Controller
            name="email"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                label={t("contact.form.email")}
                placeholder={t("contact.form.email")}
                autoComplete={"email"}
                error={fieldState?.error?.message}
                onCopy={(evt) => onCopyHandler(evt)}
                onPaste={(evt) => onPasteHandler(evt)}
                required
              />
            )}
          />
        </div>
        <div className="field email2">
          <Controller
            name="email2"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <TextField
                {...field}
                label={t("contact.form.email2")}
                placeholder={t("contact.form.email2")}
                autoComplete={"email2"}
                error={fieldState?.error?.message}
                onCopy={(evt) => onCopyHandler(evt)}
                onPaste={(evt) => onPasteHandler(evt)}
                required
              />
            )}
          />
        </div>
        <div className="field phone">
          <Controller
            name="tlf"
            control={control}
            disabled={disabled}
            render={({
              field: { id, name, onBlur, ...rest },
              fieldState,
              formState,
            }) => (
              <PhoneField
                {...rest}
                inputProps={{
                  id: id,
                  name: name,
                  onBlur: onBlur,
                  autoComplete: "tel",
                }}
                defaultCountry={defaultCountry}
                label={t("contact.form.phone")}
                placeholder={t("contact.form.phone")}
                error={fieldState?.error?.message}
              />
            )}
          />
        </div>
        <div className="field reason">
          <Controller
            name="reason"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <SelectField
                {...field}
                options={options}
                isDisabled={field.disabled}
                isClearable={true}
                isSearchable={true}
                required
                label={t("contact.form.reason")}
                placeholder={t("contact.form.reasonph")}
                error={fieldState?.error?.message}
              />
            )}
          />
        </div>
        <div className="field message">
          <Controller
            name="message"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <TextAreaField
                {...field}
                style={textAreaStyle}
                label={t("contact.form.message")}
                error={fieldState?.error?.message}
                required
              />
            )}
          />
        </div>
        <div className="field consent">
          <Controller
            name="consent"
            control={control}
            disabled={disabled}
            render={({ field, fieldState, formState }) => (
              <CheckboxField {...field} error={fieldState?.error?.message}>
                <ConsentLabel
                  onClick={(evt, type) => onClickConsentHandler(evt, type)}
                />
              </CheckboxField>
            )}
          />
        </div>
        <div className="field actions">
          <Button
            type="submit"
            text
            primary
            loading={loading}
            disabled={disabledSubmit}
          >
            <span className="label">{t("button.send")}</span>
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ContactForm;
